/**
 * When a page can be sent to an approver for deletion, withdrawal or review.
 * A request replaces the page's state, so making one while something else is
 * waiting (a review, an approved version not yet deployed, a scheduled
 * deployment, another request) would silently drop that. The server enforces
 * this; the admin screens use it to explain why an action is unavailable.
 */
export type PageRequestKind = 'delete' | 'withdraw' | 'review';

type PageLike = { state?: string | null; publishedVersion?: string | null };

const WAITING: Record<string, string> = {
  UNDER_REVIEW: 'A version of this page is waiting for review.',
  PENDING_DEPLOY: 'An approved version of this page is waiting to be deployed.',
  SCHEDULED: 'A deployment of this page is scheduled.',
  PENDING_DELETE: 'Deletion of this page is waiting for an approver.',
  PENDING_REMOVAL: 'Withdrawal of this page is waiting for an approver.',
  DELETED: 'This page is deleted.'
};

const has = (o: Record<string, string>, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** Why the request can't be made now, or null when it can. */
export function pageRequestBlocker(page: PageLike, kind: PageRequestKind): string | null {
  const state = page.state || '';
  if (kind === 'review') {
    // An approved version waiting for deployment may be replaced by a new request
    // (requestPublishPage allows it); anything else waiting may not.
    return state !== 'PENDING_DEPLOY' && has(WAITING, state) ? WAITING[state] : null;
  }
  if (has(WAITING, state)) return WAITING[state];
  if (state === 'REMOVED' && page.publishedVersion) return 'This page is withdrawn from the site at the next deployment.';
  if (kind === 'withdraw' && (!page.publishedVersion || state === 'REMOVED')) return 'This page isn’t on the site.';
  return null;
}
